import get from 'lodash/get';
import React from 'react';
import { Controller } from 'react-hook-form';
import { Alert } from '../../../../../../CommonComponents/OPL/Alert/Alert';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { InputNumberMask } from '../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { ModalBody } from '../../../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalFooter } from '../../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { Select } from '../../../../../../CommonComponents/OPL/Select/Select/Select';
import { TaxMode, WithdrawParams } from '../../../../api/useWithdrawMutation';
import {
  DistributionReasonDTO,
  DistributionWithholdingUnitDTO,
  StateWitholdingDetailsDTO,
} from '../../../../api/useWithdrawalOptionsQuery';
import { taxOptions } from './WithdrawForm.meta';
import { WithdrawMetadata, useWithdrawForm } from './useWithdrawForm';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { formatCurrency, formatPercentage } from '../../../../../../CommonUtils/formatNumber';

interface Props {
  isVisible: boolean;
  isIraAccount?: boolean;
  distributionReason?: DistributionReasonDTO;
  stateWitholdingDetails: StateWitholdingDetailsDTO | null;
  onClose(): void;
  onBack(): void;
  onSuccess({ params, metadata }: { params: WithdrawParams; metadata: WithdrawMetadata }): void;
}

export const WithdrawForm: React.FC<Props> = ({
  isVisible,
  isIraAccount = false,
  distributionReason,
  stateWitholdingDetails,
  onClose,
  onBack,
  onSuccess,
}) => {
  const { data, actions } = useWithdrawForm({
    isIraAccount,
    distributionReason,
    stateWitholdingDetails,
    onSuccess,
  });

  if (!data) {
    return null;
  }

  const { asyncData, formBag, withdrawImpediment } = data;
  const {
    control,
    formState: { errors, isSubmitting: isWithdrawing },
  } = formBag;

  const { onSubmit } = actions;

  if (!asyncData) {
    return null;
  }

  const { achRelationshipsOptions } = asyncData;

  const [federalTaxMode, stateTaxMode, stateTax, federalTax, amount] = formBag.watch([
    'federalTaxMode',
    'stateTaxMode',
    'stateTax',
    'federalTax',
    'amount',
  ]);

  const isStateRelativeToFederal = !!stateWitholdingDetails?.percentageRelativeToFederalTax;

  const parsedAmount = parseFloat(amount) || 0;
  const parsedFederalTax = parseFloat(federalTax) || 0;
  const parsedStateTax = parseFloat(stateTax) || 0;
  const federalTaxAmount =
    federalTaxMode === TaxMode.Amount ? parsedFederalTax : parsedAmount * (parsedFederalTax / 100);

  return (
    <Box
      display={isVisible ? 'block' : 'none'}
      as="form"
      mt="16"
      onSubmit={formBag.handleSubmit(onSubmit)}
    >
      <ModalBody>
        {withdrawImpediment && (
          <Alert message={withdrawImpediment.message} variant={withdrawImpediment.level} mb="16" />
        )}

        <Controller
          control={formBag.control}
          name="toRelationship"
          render={({ field: { value } }) => {
            return (
              <Select
                label="Account"
                value={value}
                options={achRelationshipsOptions}
                onChange={opt => {
                  formBag.setValue('toRelationship', opt);
                  formBag.clearErrors('toRelationship');
                }}
                formVariant={errors.toRelationship ? 'error' : undefined}
                helperText={get(errors, 'toRelationship.message')}
                isPortal
              />
            );
          }}
        />

        <Controller
          control={control}
          name="amount"
          render={({ field: { value, onChange } }) => (
            <InputNumberMask
              label="Amount"
              leftIcon="far fa-dollar-sign"
              name="amount"
              value={value}
              formVariant={errors.amount ? 'error' : undefined}
              helperText={errors.amount?.message}
              onValueChange={({ floatValue }) => {
                onChange(floatValue);
              }}
              mt="16"
            />
          )}
        />

        {distributionReason?.hasFederalTaxWithholding && (
          <>
            <Controller
              control={control}
              name="federalTax"
              render={({ field: { value, onChange } }) => (
                <InputNumberMask
                  name="federalTax"
                  value={value}
                  label="Federal Tax Withholding"
                  leftIcon={federalTaxMode === TaxMode.Amount ? 'far fa-dollar-sign' : undefined}
                  formVariant={errors.federalTax ? 'error' : undefined}
                  helperText={errors.federalTax?.message}
                  selectorProps={{
                    defaultValue: taxOptions[0],
                    options: taxOptions,
                    onChange(opt) {
                      formBag.setValue('federalTaxMode', opt.value);
                    },
                    ariaLabel: 'Federal Tax Withholding Mode selector',
                  }}
                  onValueChange={({ floatValue }) => {
                    onChange(floatValue);
                  }}
                  mt="16"
                />
              )}
            />
            <input type="hidden" {...formBag.register('federalTaxMode')} />
          </>
        )}

        {distributionReason !== undefined && (
          <>
            <Controller
              control={control}
              name="stateTax"
              render={({ field: { value, onChange } }) => (
                <InputNumberMask
                  name="stateTax"
                  value={distributionReason.hasStateTaxWithholding ? value : ''}
                  label="State Tax Withholding"
                  disabled={!distributionReason.hasStateTaxWithholding}
                  leftIcon={stateTaxMode === TaxMode.Amount ? 'far fa-dollar-sign' : undefined}
                  rightIcon={stateTaxMode === TaxMode.Percentage ? 'far fa-percent' : undefined}
                  formVariant={errors.stateTax ? 'error' : undefined}
                  helperText={errors.stateTax?.message}
                  selectorProps={
                    distributionReason.hasStateTaxWithholding &&
                    (stateWitholdingDetails === null ||
                      stateWitholdingDetails.amountType === DistributionWithholdingUnitDTO.Any)
                      ? {
                          defaultValue:
                            taxOptions.find(o => o.value === stateTaxMode) ?? taxOptions[0],
                          options: taxOptions,
                          onChange(opt) {
                            formBag.setValue('stateTaxMode', opt.value);
                          },
                          ariaLabel: 'State Tax Withholding Mode selector',
                        }
                      : undefined
                  }
                  onValueChange={({ floatValue }) => {
                    onChange(floatValue ?? '');
                  }}
                  mt="16"
                />
              )}
            />
            <input type="hidden" {...formBag.register('stateTaxMode')} />

            {stateTaxMode === TaxMode.Percentage && parsedAmount > 0 && (
              <Text mt="8" fontSize="small" color="neutral.500">
                Your state tax witholding is{' '}
                <strong>
                  {formatPercentage(parsedStateTax / 100)} (
                  {formatCurrency(
                    (parsedStateTax / 100) *
                      (isStateRelativeToFederal ? federalTaxAmount : parsedAmount),
                    { decimalPlaces: 2 },
                  )}
                  )
                </strong>{' '}
                of{' '}
                {isStateRelativeToFederal
                  ? `your federal tax witholding (${formatCurrency(federalTaxAmount, {
                      decimalPlaces: 2,
                    })})`
                  : `the withdrawal amount (${formatCurrency(parsedAmount, { decimalPlaces: 2 })})`}
              </Text>
            )}
          </>
        )}
      </ModalBody>

      <ModalFooter>
        <Box flex={1}>
          <Button leftIcon="fas fa-chevron-left" variant="secondary" onClick={onBack}>
            Back
          </Button>
        </Box>

        <Button variant="subtleInactive" onClick={onClose} mr="8">
          Cancel
        </Button>

        <Button type="submit" isLoading={isWithdrawing}>
          Continue
        </Button>
      </ModalFooter>
    </Box>
  );
};
